import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import AuthRoutes from "Routes/Components/Auth";
import NoAuthRoutes from "Routes/Components/NoAuth";

import { HTTPErrorCallback } from "Helpers/Error";
import { getPasswordSecurityVerifications } from "Helpers/Password";

import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import CaptchaWidget from "Components/Common/CaptchaWidget";
import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import SafePassword from "Components/Global/SafePassword";
import TypographyHead from "Components/Global/Typography/Head";

import * as S from "./styles";

const NewPassword = ({ loading, setLoading }) => {
  const formRef = useRef();
  const { api } = UseServices();
  const { showNotification } = UseNotification();

  const navigate = useNavigate();

  const turnstileRef = useRef(null);

  const [token, setToken] = useState(null);
  const [password, setPassword] = useState("");
  const [reCaptchaToken, setReCaptchaToken] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const tokenFromUrl = urlParams.get("token");
    verifyToken(tokenFromUrl);

    // eslint-disable-next-line
  }, [window.location.search]);

  const verifyToken = (token) => {
    setLoading(true);

    api
      .post(
        "/sessions/password/validate-token",
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(() => {
        setLoading(false);
        setToken(token);
      })
      .catch((error) => {
        HTTPErrorCallback(error);

        navigate(NoAuthRoutes.Login.path);
        setLoading(false);
      });
  };

  const submitNewPassword = (data) => {
    if (data.password !== data.repeat_password) {
      return formRef.current.setErrors({
        password: JSON.stringify({
          type: "error",
          message: "As senhas não coincidem!",
        }),
      });
    }

    if (!reCaptchaToken) {
      return showNotification(
        "warning",
        "Atenção!",
        "É necessário confirmar que você não é um robô.",
      );
    }

    setLoading(true);
    const verifications = getPasswordSecurityVerifications(data.password);

    if (
      !verifications.hasEnoughLength ||
      !verifications.hasNumber ||
      !verifications.hasUppercase ||
      !verifications.hasSpecialCharacters
    ) {
      setLoading(false);
      return formRef.current.setErrors({
        password: JSON.stringify({
          type: "error",
          message: "A senha não atende aos requisitos mínimos de segurança!",
        }),
      });
    }

    api
      .post(
        "/sessions/password/change",
        {
          password: data.password,
          captcha_token: reCaptchaToken,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(() => {
        setLoading(false);

        showNotification("success", "Sucesso!", `Senha alterada com sucesso!`);
        navigate(AuthRoutes.Dashboard.path);
      })
      .catch((error) => {
        HTTPErrorCallback(error);
        setLoading(false);
        setReCaptchaToken(null);
        turnstileRef.current?.reset();
      });
  };

  return (
    <S.Wrapper>
      <S.Row>
        <TypographyHead
          dark
          title="Nova senha"
          description="Digite a nova senha para acessar sua conta."
        />
      </S.Row>
      <S.Form onSubmit={submitNewPassword} ref={formRef}>
        <S.InputWrapper>
          <div>
            <label
              htmlFor="password"
              style={{ color: "black", fontWeight: 500 }}
            >
              Nova senha
            </label>
            <Input
              type="password"
              required
              name="password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="*******"
            />
          </div>
          <div>
            <label
              htmlFor="repeat_password"
              style={{ color: "black", fontWeight: 500 }}
            >
              Repita a senha
            </label>
            <Input
              type="password"
              required
              name="repeat_password"
              placeholder="*******"
            />
          </div>
        </S.InputWrapper>

        <SafePassword password={password} color="black" />
        <CaptchaWidget
          ref={turnstileRef}
          onSuccess={setReCaptchaToken}
          options={{
            theme: "light",
            size: "normal",
          }}
        />
        <S.ButtonsWrapper>
          <Button type="submit" text="Enviar" disabled={loading} />
          <Button
            type="button"
            outline
            text="Voltar"
            onClick={() => navigate(AuthRoutes.Dashboard.path)}
            icon={{
              name: "ChevronLeft",
              position: "left",
            }}
            style={{ color: "black" }}
          />
        </S.ButtonsWrapper>
      </S.Form>
    </S.Wrapper>
  );
};

export default NewPassword;
